import { useFlatfile } from "@flatfile/react";
import {
  useCurrentScreenSize,
  WSActionsButton,
  WSActionsProps,
  WSMenuIconItem,
  WSMenuItem,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import { Batch, BatchStatus } from "@wingspanhq/wingspan-api-sdk";
import sortBy from "lodash/sortBy";
import { useMemo } from "react";

import { useModalConfirmImport } from "../components/ModalConfirmImport";
import { useModalConfirmImportBatchDelete } from "../components/ModalConfirmImportBatchDelete";
import { selectorIsImportBatchApiUpload } from "../selectors/selectorIsImportBatchApiUpload";
import { selectorIsImportBatchFileUploaded } from "../selectors/selectorIsImportBatchFileUploaded";
import { selectorIsImportBatchFullyImported } from "../selectors/selectorIsImportBatchFullyImported";
import { selectorIsImportBatchPartiallyImported } from "../selectors/selectorIsImportBatchPartiallyImported";
import { selectorIsImportBatchUploadFinished } from "../selectors/selectorIsImportBatchUploadFinished";
import { selectorIsImportBatchUploadInProgress } from "../selectors/selectorIsImportBatchUploadInProgress";

export type ImportBatchActionsProps = {
  batch?: Batch;
  isEmptyBatch: boolean;
  /** `BatchSummary.progress.failed`. */
  failedCount?: number;
  /** Singular noun for what the batch imports, e.g. "Payable". */
  entityLabel: string;
  /** Plural noun for what the batch imports, e.g. "payables". */
  entityLabelPlural: string;
  /** Info-box copy of the delete modal. */
  deleteDescription: string;
  deleteEmptyBatchDescription: string;
  downloadExampleCSV: () => void;
  downloadErrorsCSV: () => void;
  onDeleted: () => void;
  hideButtonOn?: WSPageToolbarAction["hideButtonOn"];
};

/**
 * The actions available on an import batch, which depend entirely on where the
 * batch is between "just created" and "imported".
 */
export function useImportBatchActions(
  props: ImportBatchActionsProps
): WSPageToolbarAction[] {
  const { openPortal } = useFlatfile();
  const modalConfirmDelete = useModalConfirmImportBatchDelete();
  const modalConfirmImport = useModalConfirmImport();
  const {
    batch,
    isEmptyBatch,
    failedCount,
    entityLabel,
    entityLabelPlural,
    deleteDescription,
    deleteEmptyBatchDescription,
    downloadExampleCSV,
    downloadErrorsCSV,
    onDeleted,
    hideButtonOn
  } = props;

  return useMemo(() => {
    if (!batch) {
      return [];
    }

    const onDelete = async () => {
      const deleted = await modalConfirmDelete.open({
        batch,
        isEmptyBatch,
        entityLabel,
        description: deleteDescription,
        emptyBatchDescription: deleteEmptyBatchDescription
      });
      if (deleted) {
        onDeleted();
      }
    };

    const commonActions: WSPageToolbarAction[] = [
      { separator: true },
      {
        label: "Delete batch",
        labelProps: { color: "red400" },
        onClick: onDelete,
        icon: "trash",
        iconProps: { color: "red400" },
        hideButtonOn
      }
    ];

    if (selectorIsImportBatchFullyImported({ batch, failedCount })) {
      return [
        {
          label: "View upload",
          onClick: () => openPortal(),
          buttonKind: "Secondary",
          icon: "view",
          hideButtonOn
        }
      ];
    }

    if (
      selectorIsImportBatchPartiallyImported({ batch, failedCount }) ||
      batch.status === BatchStatus.FAILED
    ) {
      return [
        // Hidden in the status widget and shown in the page toolbar.
        {
          label: "View upload",
          onClick: () => openPortal(),
          buttonKind: "Secondary",
          icon: "view",
          name: "viewUploadInToolbar",
          hideButtonOn
        },
        // Shown in the status widget and hidden in the page toolbar.
        {
          label: "Download failed imports",
          onClick: downloadErrorsCSV,
          buttonKind: "Secondary",
          buttonOrder: 1,
          icon: "download",
          name: "downloadErrorsCSV",
          hideButtonOn
        }
      ];
    }

    if (
      batch.status === BatchStatus.PENDING ||
      batch.status === BatchStatus.PROCESSING
    ) {
      return [
        {
          label: "View upload",
          onClick: () => openPortal(),
          buttonKind: "Secondary",
          icon: "view",
          hideButtonOn
        }
      ];
    }

    if (selectorIsImportBatchUploadFinished({ batch, isEmptyBatch })) {
      return [
        {
          label: `Import ${entityLabelPlural}`,
          onClick: () =>
            modalConfirmImport.open({
              batchId: batch.id as string,
              entityLabelPlural,
              title: `Import ${entityLabelPlural}`
            }),
          buttonKind: "Primary",
          icon: "user",
          hideButtonOn
        },
        ...(selectorIsImportBatchApiUpload(batch)
          ? []
          : [
              {
                label: "View upload",
                onClick: () => openPortal(),
                buttonKind: "Secondary",
                buttonOrder: 1,
                icon: "view",
                hideButtonOn
              }
            ]),
        ...commonActions
      ] as WSPageToolbarAction[];
    }

    if (selectorIsImportBatchUploadInProgress(batch)) {
      return [
        {
          label: "View upload progress",
          onClick: () => openPortal(),
          buttonKind: "Secondary",
          icon: "view",
          hideButtonOn
        },
        ...commonActions
      ];
    }

    if (selectorIsImportBatchFileUploaded(batch)) {
      return [
        {
          label: "View upload",
          onClick: () => openPortal(),
          buttonKind: "Secondary",
          icon: "view",
          hideButtonOn
        },
        ...commonActions
      ];
    }

    return [
      {
        label: `Upload ${entityLabelPlural}`,
        onClick: () => openPortal(),
        buttonKind: "Primary",
        icon: "upload",
        hideButtonOn
      },
      {
        label: "Download example",
        onClick: downloadExampleCSV,
        buttonKind: "Secondary",
        buttonOrder: 1,
        icon: "download",
        hideButtonOn
      },
      ...commonActions
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    batch,
    isEmptyBatch,
    failedCount,
    entityLabel,
    entityLabelPlural,
    deleteDescription,
    deleteEmptyBatchDescription,
    downloadExampleCSV,
    downloadErrorsCSV,
    hideButtonOn,
    openPortal
  ]);
}

/**
 * The same action set split the way `WSCard` wants it: everything in the
 * overflow menu, and the subset that also deserves a button.
 */
export function useImportBatchStatusActions(
  props: ImportBatchActionsProps
): WSActionsProps {
  const actions = useImportBatchActions(props);
  const currentScreenSize = useCurrentScreenSize();

  const menuItems: WSMenuItem[] = useMemo(
    () =>
      actions.map(
        ({ buttonKind, hideButtonOn, buttonOrder, ...menuItem }) => menuItem
      ),
    [actions]
  );

  const buttons: WSActionsButton[] = useMemo(() => {
    const actionButtons: WSActionsButton[] = [];
    const filteredActions = actions.filter(action => action.buttonKind);

    sortBy(filteredActions, ({ buttonOrder = 1 }) => buttonOrder).forEach(
      ({ buttonKind, buttonOrder, hideButtonOn = ["XS"], ...action }) => {
        const buttonProps = action as WSMenuIconItem;

        const sharedProps = {
          onClick: buttonProps.onClick,
          onAsyncClick: action.onAsyncClick,
          loading: action.loading,
          label: buttonProps.label,
          icon: buttonProps.icon,
          kind: buttonKind,
          ...action
        } as WSActionsButton;

        const isVisible = Array.isArray(hideButtonOn)
          ? !hideButtonOn.includes(currentScreenSize)
          : true;

        if (buttonKind && isVisible) {
          actionButtons.push({
            ...sharedProps,
            label: buttonProps.label as string
          });
        }
      }
    );

    return actionButtons;
  }, [actions, currentScreenSize]);

  return {
    menuItems,
    buttons: buttons.filter(btn => btn.name !== "viewUploadInToolbar")
  };
}
